import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { createPortal } from 'react-dom';

const DEFAULT_OPTIONS = [
  { value: 'BRL', label: 'BRL', symbol: 'R$' },
  { value: 'USD', label: 'USD', symbol: '$' },
  { value: 'EUR', label: 'EUR', symbol: '€' },
];

const DropdownCurrency = ({ 
  selectedValue = 'BRL',
  onSelect = () => {},
  disabled = false,
  className = '',
  usePortal = true,
  options = null,
}) => {
  // ###### STATES
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // ###### CURRENCY OPTIONS (from context/prop or fallback)
  const currencyOptions = options && options.length > 0 ? options : DEFAULT_OPTIONS;

  // ###### EFFECTS
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        (buttonRef.current && buttonRef.current.contains(event.target)) ||
        (dropdownRef.current && dropdownRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Calculate portal position
  const getPortalStyle = () => {
    if (!buttonRef.current) return {};
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      zIndex: 20,
      minWidth: rect.width,
    };
  };

  // ###### HANDLERS
  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleSelect = (value) => {
    onSelect(value);
    setIsOpen(false);
  };

  // ###### RENDER
  return (
    <div className={`relative ${className}`}>
      {/* Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          inline-flex justify-between items-center w-14 px-1 py-1.5 
          border rounded-md text-xs font-medium text-center
          bg-gray-50 border-gray-300
          hover:bg-gray-100 hover:border-gray-400 
          focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 
          transition-all
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}
        `}
      >
        <span className="truncate">
          {selectedValue}
        </span>
        <ChevronDownIcon 
          className={`ml-1 h-3 w-3 text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        usePortal
          ? createPortal(
              <div style={getPortalStyle()}>
                <div
                  ref={dropdownRef}
                  className="w-20 rounded-md bg-white shadow-lg border border-gray-200"
                >
                  {currencyOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => handleSelect(option.value)}
                      className={`
                        cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-50 
                        ${selectedValue === option.value ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <span>{option.label}</span>
                        <span className="text-gray-500">{option.symbol}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>,
              document.body
            )
          : (
              <div
                ref={dropdownRef}
                className="absolute top-full right-0 z-10 mt-1 w-20 rounded-md bg-white shadow-lg border border-gray-200"
              >
                {currencyOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={`
                      cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-50 
                      ${selectedValue === option.value ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option.label}</span>
                      <span className="text-gray-500">{option.symbol}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
      )}
    </div>
  );
};

export default DropdownCurrency;
